import { useEffect, useState } from 'react';
import { useDebounce } from 'ahooks';

/**
 * A search has to be longer than two characters before it is worth running. One
 * or two letters match most of a supplier's library, so the reader pays a round
 * trip and a re-render to be shown almost everything they already had.
 *
 * The same floor is applied server-side in ProfessionalDownloadLibraryService,
 * so a search too short to narrow anything is refused whichever way it arrives.
 */
export const DOWNLOAD_SEARCH_MIN_CHARS = 3;

/** Long enough to cover typing a word, short enough not to feel stuck. */
export const DOWNLOAD_SEARCH_DEBOUNCE_MS = 600;

/**
 * Whether a typed search should be applied.
 *
 * Clearing always applies, otherwise the reader could not get back to the full
 * list without typing four characters first.
 */
export const shouldApplyDownloadSearch = (search: string): boolean =>
    search.length === 0 || search.length >= DOWNLOAD_SEARCH_MIN_CHARS;

/**
 * Keeps the search box responsive while the URL, and the query behind it, move
 * only once the reader pauses.
 *
 * The box is driven from local state so every keystroke still shows; the
 * committed value is the one in the URL, and it is written back only after the
 * debounce and only when the search is worth running.
 */
export const useDownloadSearch = (
    committed: string,
    onCommit: (search: string) => void,
) => {
    const [keyword, setKeyword] = useState(committed);
    const debounced = useDebounce(keyword, {
        wait: DOWNLOAD_SEARCH_DEBOUNCE_MS,
    });

    // The URL is the source of truth: a back navigation or a cleared filter has
    // to pull the box back into step rather than leave the old word showing.
    useEffect(() => setKeyword(committed), [committed]);

    useEffect(() => {
        const next = debounced.trim();

        if (next === committed || !shouldApplyDownloadSearch(next)) return;

        onCommit(next);
        // `committed` is the value this effect writes; re-running on it would
        // fight the reader's typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    return { keyword, setKeyword };
};
